import { api } from "./client";
import type {
  ChatAttachment,
  ChatImportCommitResult,
  ChatImportMessageInput,
  ChatImportParticipants,
  ChatImportRun,
  PortalInviteResponse,
  BuilderInviteResponse,
} from "@/types";

/** GET {Jobs}/staff/chat/import/participants?job_id — the identities a Moxo
 *  export's sender names can map onto (staff roster + the job's portal users),
 *  plus prior imports for the double-import warning. */
export function getImportParticipants(
  jobId: number,
  signal?: AbortSignal,
): Promise<ChatImportParticipants> {
  return api.get<ChatImportParticipants>("jobs", "staff/chat/import/participants", {
    query: { job_id: jobId },
    signal,
  });
}

/** GET {Jobs}/staff/chat/imports — every completed import, newest first. */
export function listImports(signal?: AbortSignal): Promise<ChatImportRun[]> {
  return api.get<ChatImportRun[]>("jobs", "staff/chat/imports", { signal });
}

/** POST {Jobs}/portal/invite with send_invite_email=false — create (or
 *  reactivate) the portal_user for a client WITHOUT emailing anything, so
 *  imported history can be attributed to clients who haven't been invited to
 *  the new portal yet. Idempotent: a repeat returns the same record. The real
 *  invite email goes out later through the normal Client-page invite flow. */
export function createPortalAccessSilently(clientId: number): Promise<PortalInviteResponse> {
  return api.post<PortalInviteResponse>("jobs", "portal/invite", {
    body: { client_id: clientId, send_invite_email: false },
  });
}

/** POST {Jobs}/builders/{id}/invite with send_invite_email=false — the builder
 *  twin of createPortalAccessSilently: create (or reactivate) the login for the
 *  firm's contact so imported history can be attributed to a builder who hasn't
 *  been invited yet. Nothing is emailed. */
export function createBuilderAccessSilently(
  builderId: number,
  name: string,
  email: string,
): Promise<BuilderInviteResponse> {
  return api.post<BuilderInviteResponse>("jobs", `builders/${builderId}/invite`, {
    body: { name, email, send_invite_email: false },
  });
}

/** POST {Jobs}/staff/chat/import/upload — stage one file pulled out of the
 *  export zip. Unlike the live chat upload, any file type is accepted and the
 *  cap is 50 MB; the row stays invisible until commit links it. */
export function uploadImportAttachment(
  jobId: number,
  file: Blob,
  fileName: string,
): Promise<ChatAttachment> {
  const fd = new FormData();
  fd.append("file", file, fileName);
  fd.append("job_id", String(jobId));
  return api.post<ChatAttachment>("jobs", "staff/chat/import/upload", { body: fd });
}

/** POST {Jobs}/staff/chat/import/commit — insert the parsed history in one
 *  transaction. Messages arrive pre-mapped to concrete sender identities; the
 *  server validates everything (no partial writes) and stores each message at
 *  its ORIGINAL timestamp born read on both sides. A board_id it has imported
 *  before is refused unless the run is an `append` (update mode — the page has
 *  already filtered to entries past the board's high-water mark, and it must
 *  target the job the board already lives in) or allowDuplicate. `lastSeq` is
 *  the payload's highest Moxo seq, recorded as the next diff's baseline. */
export function commitImport(input: {
  jobId: number;
  boardId: string;
  boardName: string;
  allowDuplicate: boolean;
  append: boolean;
  lastSeq: number;
  messages: ChatImportMessageInput[];
}): Promise<ChatImportCommitResult> {
  return api.post<ChatImportCommitResult>("jobs", "staff/chat/import/commit", {
    body: {
      job_id: input.jobId,
      board_id: input.boardId,
      board_name: input.boardName,
      allow_duplicate: input.allowDuplicate,
      append: input.append,
      last_seq: input.lastSeq,
      messages: input.messages,
    },
  });
}
